import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import PrivateRoute from './components/PrivateRoute';
import SpecialUserRoute from './components/SpecialUserRoute';
import AppShell from './components/AppShell';
import AdminRoute from './components/AdminRoute';
import RoleRoute from './components/RoleRoute';
import { canViewTeam, canViewSales } from './utils/roles';
import { useNotifications } from './context/NotificationContext';
import { EventDetailsDialog } from './components/EventDetailsDialog';
import { useAuth } from './context/AuthContext';

// Страницы грузятся лениво — каждая уходит в свой chunk,
// тяжёлые зависимости (chart.js, leaflet, pdf.js) не попадают в основной бандл.
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MapPage = lazy(() => import('./pages/MapPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const InventoryDashboardPage = lazy(() => import('./pages/InventoryDashboardPage'));
const LiveMapPage = lazy(() => import('./pages/LiveMapPage'));
const SendLocationPage = lazy(() => import('./pages/SendLocationPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const DoctorsPage = lazy(() => import('./pages/DoctorsPage'));
const PharmaciesPage = lazy(() => import('./pages/PharmaciesPage'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const CatalogPage = lazy(() => import('./pages/CatalogPage'));
const SalesAnalyticsPage = lazy(() => import('./pages/SalesAnalyticsPage'));
const SalesDashboardPage = lazy(() => import('./pages/SalesDashboardPage'));
const SpecialitiesPage = lazy(() => import('./pages/SpecialitiesPage'));
const HospitalsPage = lazy(() => import('./pages/HospitalsPage'));
const DoctorsCleanupPage = lazy(() => import('./pages/DoctorsCleanupPage'));
const ChangeLogPage = lazy(() => import('./pages/ChangeLogPage'));
const CoveragePage = lazy(() => import('./pages/CoveragePage'));
const WorkspacePage = lazy(() => import('./pages/WorkspacePage'));

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

function App() {
  const { user } = useAuth();
  const { eventDetailsToShow, setEventDetailsToShow } = useNotifications();

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Публичные экраны — без каркаса */}
          <Route path="/login" element={<Login />} />

          {/* Приложение — внутри общего каркаса (боковая панель + шапка) */}
          <Route element={<AppShell />}>
            <Route path="/" element={<Navigate to={user ? '/overview' : '/login'} replace />} />
            <Route path="/overview" element={<PrivateRoute><WorkspacePage /></PrivateRoute>} />
            <Route path="/team" element={<RoleRoute allow={canViewTeam}><WorkspacePage teamOnly /></RoleRoute>} />
            <Route path="/register" element={<SpecialUserRoute><Register /></SpecialUserRoute>} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/catalog" element={<PrivateRoute><CatalogPage /></PrivateRoute>} />
            <Route path="/map" element={<PrivateRoute><MapPage /></PrivateRoute>} />
            <Route path="/calendar" element={<PrivateRoute><CalendarPage /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
            <Route path="/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
            <Route path="/inventory" element={<RoleRoute allow={canViewSales}><InventoryDashboardPage /></RoleRoute>} />
            <Route path="/send-location" element={<PrivateRoute><SendLocationPage /></PrivateRoute>} />
            <Route path="/change-password" element={<PrivateRoute><ChangePasswordPage /></PrivateRoute>} />
            <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
            <Route path="/coverage" element={<PrivateRoute><CoveragePage /></PrivateRoute>} />
            <Route path="/doctors" element={<PrivateRoute><DoctorsPage /></PrivateRoute>} />
            <Route path="/doctors/cleanup" element={<AdminRoute><DoctorsCleanupPage /></AdminRoute>} />
            <Route path="/pharmacies" element={<PrivateRoute><PharmaciesPage /></PrivateRoute>} />
            <Route path="/reps-map" element={<RoleRoute allow={canViewTeam}><LiveMapPage /></RoleRoute>} />
            <Route path="/import" element={<AdminRoute><ImportPage /></AdminRoute>} />
            <Route path="/specialities" element={<AdminRoute><SpecialitiesPage /></AdminRoute>} />
            <Route path="/hospitals" element={<AdminRoute><HospitalsPage /></AdminRoute>} />
            <Route path="/analytics/sales-lite" element={<RoleRoute allow={canViewSales}><SalesAnalyticsPage /></RoleRoute>} />
            <Route path="/analytics/sales" element={<RoleRoute allow={canViewSales}><SalesDashboardPage /></RoleRoute>} />
            <Route path="/changelog" element={<AdminRoute><ChangeLogPage /></AdminRoute>} />
          </Route>
        </Routes>
      </Suspense>

      <EventDetailsDialog
        event={eventDetailsToShow}
        onClose={() => setEventDetailsToShow(null)}
      />
    </Router>
  );
}

export default App;
